import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Scales, Check, Warning, Drop, ArrowSquareOut, Lock, LockOpen, CalendarBlank } from "@phosphor-icons/react";

/**
 * LegalProtocolPanel — banner + checklist + moisture reading form for any WO
 * that carries `legal_protocol.enabled=true`. Auto-attached to Fair Housing /
 * Moisture / Mold / Habitability / Water Intrusion / ADA WOs on create.
 *
 * Renders inside JobDetail. Tech ack's the protocol, ticks checklist items,
 * logs moisture readings. Nothing gets lost.
 */
export default function LegalProtocolPanel({ jobId }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState({ location: "", dry: false, reading_value: "", note: "" });
  const [freezing, setFreezing] = useState(false);
  const [showClockModal, setShowClockModal] = useState(false);

  const load = () => {
    api.get(`/jobs/${jobId}/legal-protocol/status`)
      .then(r => setState(r.data))
      .catch(() => setState({ legal_protocol: { enabled: false } }));
  };
  useEffect(load, [jobId]);

  if (!state) return null;
  const lp = state.legal_protocol || {};
  if (!lp.enabled) {
    // Tag-as-Legal affordance — small pill that lets office retroactively
    // enable a Legal Protocol on any WO (including completed ones). Powers
    // the "1026 Irving Wall Leaking closed months ago, now I need a
    // compliance packet" flow.
    return <TagAsLegalButton jobId={jobId} onTagged={load}/>;
  }
  const st = state.status || {};

  const tone = st.bucket === "red" ? "border-red-500 bg-red-50"
    : st.bucket === "amber" ? "border-amber-500 bg-amber-50"
    : "border-emerald-500 bg-emerald-50";

  const ack = async (checklist_id, done) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/legal-protocol/acknowledge`, { checklist_id, done });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const submitReading = async () => {
    if (!reading.location.trim()) return toast.error("Location required");
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/legal-protocol/moisture-reading`, {
        location: reading.location.trim(),
        dry: !!reading.dry,
        reading_value: reading.reading_value === "" ? null : parseFloat(reading.reading_value),
        unit: "%",
        note: reading.note.trim(),
      });
      toast.success("Reading logged");
      setReading({ location: "", dry: false, reading_value: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const ackAll = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/legal-protocol/acknowledge`, { done: true });
      toast.success("Protocol acknowledged");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  // Toggle the per-WO freeze flag. Frozen WOs skip checklist migrations
  // — critical for jobs in a final stage where the tech needs a stable
  // set of items even if the template evolves in the background.
  const toggleFreeze = async () => {
    if (freezing) return;
    setFreezing(true);
    try {
      const r = await api.post(`/jobs/${jobId}/legal-protocol/freeze`, {
        frozen: !lp.freeze,
      });
      toast.success(r.data.frozen ? "Checklist frozen — safe for final stage" : "Checklist unfrozen");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Freeze toggle failed"));
    } finally { setFreezing(false); }
  };

  return (
    <div className={`mt-3 rugged-card border-l-8 ${tone}`} data-testid="legal-protocol-panel">
      <div className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Scales weight="fill" size={20} className="text-red-700"/>
          <div className="font-display font-black text-base uppercase tracking-tight">Legal Protocol · {lp.category_label}</div>
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">Documentation required</span>
          {st.satisfied && (
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white inline-flex items-center gap-0.5">
              <Check weight="bold" size={10}/> Evidence complete
            </span>
          )}
          {lp.freeze && (
            <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-700 text-white inline-flex items-center gap-0.5" title={`Frozen ${lp.freeze_toggled_at ? "at " + new Date(lp.freeze_toggled_at).toLocaleString() : ""}`}>
              <Lock weight="fill" size={10}/> Frozen
            </span>
          )}
          <div className="ml-auto flex items-center gap-1 flex-wrap">
            {lp.category_key === "habitability" && (
              <button
                onClick={() => setShowClockModal(true)}
                data-testid="legal-adjust-clock-btn"
                title="Override the 30-day start date if the WO was logged later than the actual report"
                className="h-6 px-2 border border-slate-400 hover:bg-white text-slate-700 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 rounded"
              >
                <CalendarBlank size={10}/>
                {state.habitability_start_override ? "Clock adjusted" : "Adjust 30-day clock"}
              </button>
            )}
            <button
              onClick={toggleFreeze}
              disabled={freezing}
              data-testid="legal-freeze-btn"
              title={lp.freeze
                ? "Unfreeze — future template updates will apply again"
                : "Freeze — safe when the WO is in a final stage and shouldn't get new checklist items bolted on"}
              className={`h-6 px-2 border font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 rounded ${lp.freeze ? "border-slate-700 bg-slate-700 text-white hover:bg-slate-800" : "border-slate-400 hover:bg-white text-slate-700"}`}
            >
              {lp.freeze ? <Lock weight="fill" size={10}/> : <LockOpen size={10}/>}
              {lp.freeze ? "Unfreeze" : "Freeze"}
            </button>
            <Link
              to={`/admin/jobs/${jobId}/legal-protocol`}
              data-testid="legal-protocol-deep-link"
              title="Open the printable compliance packet on its own page"
              className="h-6 px-2 border border-slate-400 hover:bg-white text-slate-700 font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1 rounded"
            >
              <ArrowSquareOut size={10}/> Full page
            </Link>
          </div>
        </div>
        <div className="mt-1 text-[11px] font-black uppercase tracking-widest text-slate-600">
          Photos {st.photos_total}/{st.photos_needed} · Checklist {st.required_done}/{st.required_total}
          {lp.requires_moisture_reading && <> · Moisture readings {(lp.moisture_readings||[]).length}</>}
          {!st.acknowledged && <span className="ml-2 text-red-700">· not acknowledged</span>}
        </div>

        {!st.acknowledged && (
          <button onClick={ackAll} disabled={busy} data-testid="legal-ack-all-btn"
            className="mt-3 h-9 px-3 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <Warning weight="fill" size={12}/> I understand — acknowledge protocol
          </button>
        )}

        <div className="mt-3 space-y-1.5">
          {(lp.checklist || []).map(c => (
            <label key={c.id} className={`flex items-start gap-2 p-2 border-2 text-sm ${c.done ? "bg-emerald-50 border-emerald-400" : "bg-white border-slate-300"} ${c.required ? "" : "opacity-70"}`}>
              <input type="checkbox" checked={!!c.done} disabled={busy}
                data-testid={`legal-check-${c.id}`}
                onChange={e => ack(c.id, e.target.checked)} className="mt-0.5"/>
              <span className="flex-1">
                <span className={c.done ? "line-through" : ""}>{c.text}</span>
                {c.required && <span className="ml-1 text-red-600 text-[10px] font-black">*REQUIRED</span>}
                {c.done && c.done_by_name && (
                  <span className="ml-2 text-[10px] text-slate-500">✓ {c.done_by_name}</span>
                )}
              </span>
            </label>
          ))}
        </div>

        {lp.requires_moisture_reading && (
          <div className="mt-3 p-3 border-2 border-cyan-500 bg-cyan-50" data-testid="moisture-reading-form">
            <div className="flex items-center gap-2 mb-2">
              <Drop weight="fill" size={14} className="text-cyan-700"/>
              <div className="font-display font-black text-sm uppercase tracking-wider">Log moisture reading</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Location (e.g. Basement N wall)" value={reading.location}
                data-testid="moisture-location-input"
                onChange={e => setReading({...reading, location: e.target.value})}
                className="h-9 px-2 border-2 border-slate-300 text-sm col-span-2"/>
              <input type="number" step="0.1" placeholder="Reading %" value={reading.reading_value}
                data-testid="moisture-value-input"
                onChange={e => setReading({...reading, reading_value: e.target.value})}
                className="h-9 px-2 border-2 border-slate-300 text-sm font-mono"/>
              <label className="flex items-center gap-2 text-sm px-2 border-2 border-slate-300 bg-white">
                <input type="checkbox" checked={reading.dry}
                  onChange={e => setReading({...reading, dry: e.target.checked})}/>
                <span>Dry — no active moisture</span>
              </label>
              <input placeholder="Notes (optional)" value={reading.note}
                onChange={e => setReading({...reading, note: e.target.value})}
                className="h-9 px-2 border-2 border-slate-300 text-sm col-span-2"/>
              <button onClick={submitReading} disabled={busy} data-testid="moisture-submit-btn"
                className="h-9 px-3 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest col-span-2">
                Log reading
              </button>
            </div>
            {(lp.moisture_readings || []).length > 0 && (
              <div className="mt-3 space-y-1">
                {(lp.moisture_readings || []).map(r => (
                  <div key={r.id} className="p-2 bg-white border border-slate-300 text-xs" data-testid={`moisture-reading-${r.id}`}>
                    <span className="font-black">{r.location}</span>
                    <span className="ml-2 font-mono">
                      {r.dry ? "DRY" : (r.reading_value != null ? `${r.reading_value}${r.unit || "%"}` : "?")}
                    </span>
                    {r.note && <span className="ml-2 text-slate-600">· {r.note}</span>}
                    <span className="ml-2 text-[10px] text-slate-500">
                      · {r.logged_by_name} · {new Date(r.logged_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {st.missing && st.missing.length > 0 && !st.satisfied && (
          <div className="mt-3 p-2 border-2 border-red-400 bg-white text-xs text-red-700">
            <b>Missing:</b> {st.missing.join(" · ")}
          </div>
        )}
      </div>

      {showClockModal && (
        <AdjustHabitabilityClockModal
          jobId={jobId}
          currentOverride={state.habitability_start_override}
          currentReason={state.habitability_start_override_reason}
          createdAt={state.created_at}
          onClose={() => setShowClockModal(false)}
          onSaved={() => { setShowClockModal(false); load(); }}
        />
      )}
    </div>
  );
}


/**
 * AdjustHabitabilityClockModal
 *
 * Lets an admin push or pull the effective "day 0" of the 30-day
 * habitability clock. Used when the WO was logged into Field Radar N
 * days AFTER the landlord/tenant actually reported the issue — otherwise
 * the countdown is wrong by that many days.
 *
 * Also exposes a "Clear override" path so admins can revert to
 * `created_at`-based math with one click.
 */
function AdjustHabitabilityClockModal({ jobId, currentOverride, currentReason, createdAt, onClose, onSaved }) {
  const initial = (() => {
    if (currentOverride) return currentOverride.slice(0, 10);
    if (createdAt) return String(createdAt).slice(0, 10);
    return "";
  })();
  const [date, setDate] = useState(initial);
  const [reason, setReason] = useState(currentReason || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!date) {
      toast.error("Pick a start date");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/habitability/set-start`,
        { start_date: date, reason: reason.trim() });
      toast.success(`30-day clock reset · ${r.data.days_left} days left`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Update failed"));
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear override and revert to WO creation date as day 0?")) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/habitability/clear-start`, {});
      toast.success("Override cleared — clock now runs from WO created_at");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Clear failed"));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="clock-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border-4 border-amber-500 rounded-lg max-w-md w-full p-5 space-y-3"
        data-testid="clock-modal"
      >
        <div className="flex items-center gap-2">
          <CalendarBlank weight="fill" size={20} className="text-amber-600"/>
          <h2 className="text-lg font-black tracking-tight">Adjust 30-day Habitability Clock</h2>
        </div>
        <div className="text-sm text-slate-700 leading-relaxed">
          Set the day the issue was first <b>reported</b> — not the day the WO was created in Field Radar. Everywhere the SLA clock is used (auto-flag on day 25, compliance PDF, Legal Radar) will use this date instead of the WO created_at.
          {createdAt && (
            <div className="mt-2 text-xs text-slate-500">
              WO created on <span className="font-mono">{String(createdAt).slice(0, 10)}</span>{currentOverride && <span> · currently overridden to <span className="font-mono">{currentOverride.slice(0, 10)}</span></span>}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Effective start date *</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            data-testid="clock-modal-date-input"
            className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-amber-500 rounded text-sm font-mono"/>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Reason (for audit) — optional</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="clock-modal-reason-input"
            placeholder="e.g. Landlord reported via email Jan 3rd"
            className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-amber-500 rounded text-sm"/>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            data-testid="clock-modal-cancel"
            className="flex-1 h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase text-xs tracking-widest rounded disabled:opacity-50">Cancel</button>
          {currentOverride && (
            <button onClick={clear} disabled={busy}
              data-testid="clock-modal-clear"
              className="flex-1 h-10 border-2 border-red-400 hover:bg-red-50 text-red-700 font-black uppercase text-xs tracking-widest rounded disabled:opacity-50">
              Clear override
            </button>
          )}
          <button onClick={save} disabled={busy}
            data-testid="clock-modal-save"
            className="flex-1 h-10 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white font-black uppercase text-xs tracking-widest rounded">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TagAsLegalButton({ jobId, onTagged }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("fair_housing");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/legal-protocol/toggle`,
        { enabled: true, category_key: category });
      toast.success(`Tagged as ${category.replace("_", " ")}`);
      setOpen(false);
      onTagged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="tag-as-legal-btn"
        className="mt-3 h-8 px-2 border-2 border-red-400 hover:bg-red-50 text-red-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
        <Scales weight="bold" size={11}/> Tag as Legal · retroactive
      </button>
    );
  }

  return (
    <div className="mt-3 p-3 border-2 border-red-500 bg-red-50" data-testid="tag-as-legal-form">
      <div className="flex items-center gap-2 mb-2">
        <Scales weight="fill" size={14} className="text-red-700"/>
        <div className="font-display font-black text-sm uppercase tracking-wider">Retroactively tag as legal</div>
      </div>
      <div className="text-[11px] text-slate-700 mb-2">
        Attaches the documentation checklist and starts tracking this WO on the Legal Radar. Existing photos + notes stay attached; you just need to satisfy the checklist to freeze a compliance packet.
      </div>
      <select value={category} onChange={e => setCategory(e.target.value)}
        data-testid="tag-as-legal-category"
        className="h-9 w-full px-2 border-2 border-slate-300 text-sm font-medium bg-white">
        <option value="fair_housing">Fair Housing</option>
        <option value="moisture">Moisture</option>
        <option value="water_intrusion">Water Intrusion</option>
        <option value="mold">Mold</option>
        <option value="habitability">Habitability</option>
        <option value="ada">ADA</option>
      </select>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={() => setOpen(false)}
          className="h-9 px-3 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-white">
          Cancel
        </button>
        <button onClick={submit} disabled={busy} data-testid="tag-as-legal-submit"
          className="h-9 px-3 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
          {busy ? "Tagging…" : "Tag now"}
        </button>
      </div>
    </div>
  );
}
